import type { AccommodationReservationProviderInput } from "../../../../../__generated__/gql/graphql";
import { useGtm } from "../../../../../lib/google-tag-manager/gtm";
import { useSiteDetailDrawer } from "../../../site-detail/site-detail-drawer-provider";
import type {
  BrowseRoomTypeWithAvailability,
  BrowseSiteWithAvailability,
} from "../../_hooks/use-room-type-search";
import {
  groupRoomTypesByArchitectureType,
  type ArchitectureTypeGroup as ArchitectureTypeGroupResult,
} from "../../_utils/group-room-types-by-architecture-type";
import { isSingleSelection } from "../../_utils/is-single-selection";
import { isArchitectureTypeGroupUnavailableForRange } from "../../_utils/room-type-availability-view";

import { ArchitectureTypeGroup } from "./architecture-type-group";
import { AvailabilityStrip } from "./availability-strip";
import { SingleSelectionArchitecture } from "./single-selection-architecture";
import { SiteCardGallery } from "./site-card-gallery";

export function SiteCard({
  site,
  priority = false,
  provider,
  facilitySlugs,
  evChargerAvailable,
  architectureTypes,
  largeOnly,
}: {
  site: BrowseSiteWithAvailability;
  /** above-the-fold の先頭カードのみ true。Hero 画像を priority 読みする（LCP 対策） */
  priority?: boolean;
  provider: AccommodationReservationProviderInput;
  facilitySlugs: string[];
  evChargerAvailable: boolean;
  architectureTypes: string[];
  largeOnly: boolean;
}) {
  const { open } = useSiteDetailDrawer();
  const gtm = useGtm();
  // 並び順（設備が少ない順）は BE が確定済み。FE は建築タイプでまとめるだけ。
  // 拠点自体の全滅は select-visible-areas.ts で除外済み。ここでは一部グループのみ全滅なケースを除外する。
  const groups = groupRoomTypesByArchitectureType(
    site.roomTypesForReservation,
  ).filter(
    (group) => !isArchitectureTypeGroupUnavailableForRange(group.roomTypes),
  );

  const handleCardClick = () => {
    gtm.sendEvent("rt_rsv_site_detail_clicked", { site_slug: site.slug });
    open({ siteSlug: site.slug });
  };

  return (
    // gap: SP=16px（Figma 準拠）、PC=24px（現状維持）
    <div className="flex flex-col gap-[16px] pt-[16px] lg:gap-[24px] lg:pt-0">
      <SiteCardHeader site={site} nameOnClick={handleCardClick} />

      <AvailabilityStrip
        siteId={site.id}
        siteSlug={site.slug}
        provider={provider}
        facilitySlugs={facilitySlugs}
        evChargerAvailable={evChargerAvailable}
        architectureTypes={architectureTypes}
        largeOnly={largeOnly}
      />

      <div className="flex flex-col gap-0 lg:flex-row lg:gap-[24px]">
        <div className="lg:max-w-[400px] lg:min-w-0 lg:flex-1">
          <SiteCardGallery
            imageUrls={site.content.heroImages.map((image) => image.rawUrl)}
            altText={site.name}
            onImageClick={handleCardClick}
            priority={priority}
          />
        </div>
        {/* グループ間 gap: SP=0（border-b divider で区切る）、PC=24px（Figma 5354-12027） */}
        <div className="flex min-w-px flex-1 flex-col gap-0 lg:min-w-[290px] lg:gap-[24px]">
          {renderGroups(groups)}
        </div>
      </div>
    </div>
  );
}

function SiteCardHeader({
  site,
  nameOnClick,
}: {
  site: BrowseSiteWithAvailability;
  nameOnClick: () => void;
}) {
  return (
    // Figma SP（5171-17491 Kyoten）: カードに py-[16px]（上端→ヘッダ 16px、ヘッダ〜写真 16px）
    <div className="bg-dx-surface-base flex items-center gap-[4px] px-[16px] lg:px-0">
      <div className="flex min-w-px flex-1 flex-col gap-[4px] lg:gap-[6px]">
        <h2 className="font-notojp text-dx-text-default text-[20px] leading-[24px] font-bold lg:text-[22px]">
          <button
            data-analytics-id="room_type_browse_opened_site_detail"
            type="button"
            onClick={nameOnClick}
            className="text-left underline"
          >
            {site.name}
          </button>
        </h2>
        {site.shortAddress && (
          <p className="font-notojp text-dx-text-default text-[12px] leading-none">
            {site.shortAddress}
          </p>
        )}
      </div>
    </div>
  );
}

function renderGroups(
  groups: ArchitectureTypeGroupResult<BrowseRoomTypeWithAvailability>[],
) {
  return groups.map((group, index) =>
    isSingleSelection(group.roomTypes) ? (
      <SingleSelectionArchitecture
        key={group.key}
        roomTypes={group.roomTypes}
        firstItem={index === 0}
      />
    ) : (
      <ArchitectureTypeGroup
        key={group.key}
        roomTypes={group.roomTypes}
        firstItem={index === 0}
      />
    ),
  );
}
